import { Fragment, useMemo } from 'react';
import styled, { ThemeColor } from 'styled-components';
import { EdnaWrapper } from '@providers/EdnaProvider';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {
  slideDownAndFade,
  slideLeftAndFade,
  slideUpAndFade,
  slideRightAndFade,
} from '@animations/styled_animation_helpers';
import { TooltipProps } from './types';

const TOOLTIP_COLOR: ThemeColor = 'grey800';

const Content = styled(TooltipPrimitive.Content)<{
  $width?: string;
  $backgroundColor?: ThemeColor;
}>`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: white;
  line-height: 1.5;
  background-color: ${(props) =>
    props.$backgroundColor || props.theme.colors[TOOLTIP_COLOR]};
  box-shadow: 1px 2px 10px 3px rgb(74 74 74 / 15%);
  padding: 10px 12px;
  border-radius: 4px;
  width: ${(props) => props.$width};
  z-index: 999999;

  a {
    color: inherit;
    text-decoration: underline;
  }

  &[data-state='delayed-open'][data-side='top'] {
    animation: ${slideDownAndFade} 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state='delayed-open'][data-side='right'] {
    animation: ${slideLeftAndFade} 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state='delayed-open'][data-side='bottom'] {
    animation: ${slideUpAndFade} 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state='delayed-open'][data-side='left'] {
    animation: ${slideRightAndFade} 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

const Arrow = styled(TooltipPrimitive.Arrow)<{ $color?: ThemeColor }>`
  fill: ${(props) => props.$color || props.theme.colors[TOOLTIP_COLOR]};
`;

const getWidth = (size?: string) => {
  if (size?.includes('px')) {
    return size;
  }

  switch (size) {
    case 'mini':
      return '100px';
    case 'small':
      return '150px';
    case 'medium':
      return '200px';
    case 'large':
      return '300px';
    default:
      return 'max-content';
  }
};

/**
 * Use this component to wrap an element that will act as the "trigger".
 * When hovering over the trigger, the tooltip will appear.
 * You can position the tooltip with the `side` and `align` props.
 *
 * Use it when you want to provide a hint or to clarify something, not provide
 * additional content (for that, use a popover). Tooltips generally should only contain text and not rich content.
 *
 * The tooltip has "collision avoidance" enabled. This means that if the tooltip
 * is triggered somewhere on the screen where it would collide with another
 * element or with the boundaries of the page (be hidden) then it will automatically
 * change sides so that it remains visible.
 *
 */
const Tooltip = ({
  children,
  side = 'top',
  sideOffset,
  align = 'center',
  alignOffset,
  backgroundColor,
  content,
  disabled = false,
  size,
  className,
  openOnActivation = false,
  open,
  onOpenChange,
  portal = true,
}: TooltipProps) => {
  const tooltipWidth = useMemo(() => getWidth(size), [size]);

  const ContentWrapper = portal ? TooltipPrimitive.Portal : Fragment;

  return disabled ? (
    <>{children}</> // don't render tooltip at all when disabled
  ) : (
    <EdnaWrapper>
      <TooltipPrimitive.Provider>
        <TooltipPrimitive.Root
          delayDuration={250}
          open={open}
          onOpenChange={onOpenChange}
        >
          <TooltipPrimitive.Trigger
            asChild
            {...(openOnActivation && { onClick: (e) => e.preventDefault() })}
          >
            {children}
          </TooltipPrimitive.Trigger>
          <ContentWrapper>
            <Content
              side={side}
              align={align}
              alignOffset={alignOffset}
              sideOffset={sideOffset}
              $width={tooltipWidth}
              $backgroundColor={backgroundColor}
              collisionPadding={4}
              className={className}
              {...(openOnActivation && {
                onPointerDownOutside: (e) => e.preventDefault(),
              })}
            >
              {content}
              <Arrow $color={backgroundColor} width={12} height={6} />
            </Content>
          </ContentWrapper>
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    </EdnaWrapper>
  );
};

export default Tooltip;
